// ViewRegularizationList.js
import React, { Fragment, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Typography, Box, Button, Badge, TextField, Grid } from "@mui/material";
import { ToastContainer } from "react-toastify";
import InfoCard from "@/pages/attendance/views/regularization/ViewRegularizationList/InfoCard";
import DataTableComponent from "@/pages/attendance/views/regularization/ViewRegularizationList/DataTableComponent";
import ApplicationTimeline from "@/pages/attendance/views/regularization/ViewRegularizationList/ApplicationTimeline";
import { CustomAlert } from "@/components";
import { v4 as uuidv4 } from "uuid";
import { Field, Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import StyledBadge from "@/components/badge/Badge"; // Adjust the import path accordingly
import { useWithdrawRegularizationMutation } from "@/api/services/attendance/regularization/EmployeeRegularization";
import { toast } from "@/components/toastify";
import { format } from "date-fns";

const ViewRegularizationList = ({ setShowViewList, selectedRecord, tabName, refetch, setSelectedRecord, refetchHistory }) => {
  const { id } = useParams();
  const [approvelist, setApprovelist] = useState({});
  const [applyedlist, setApplyedlist] = useState([]);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const [openWithdrawPopUp, setOpenWithdrawPopUp] = useState(false);
  const withdrawClose = () => setOpenWithdrawPopUp(false);
  const [triggerGetData] = useWithdrawRegularizationMutation();

  useEffect(() => {
    const fetchedData = selectedRecord;

    if (!fetchedData) return;

    // Add unique IDs to applied regularizations
    const rowsWithIds = fetchedData.appliedRegularizations?.map((row) => ({
      ...row,
      id: uuidv4(),
    })) || [];

    setApprovelist(fetchedData);
    setApplyedlist(rowsWithIds);

    if (fetchedData.status === "Approved") {
      // setshowWithdraw(true);
    }
  }, [id, selectedRecord]);

  const withdraw = () => {
    toast.success("Withdraw Successfully.");
    setTimeout(() => {
      navigate("/attendance/regularization");
    }, 3000);
  };

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No",
      flex: 1,
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems={"center"} height={"100%"}>
            {params.api.getAllRowIds().indexOf(params.id) + 1}
            {/* {params.api.getRowIndexRelativeToVisibleRows(params.id) + 1} */}
          </Box>
        );
      },
    },
    {
      field: "attendanceDate",
      headerName: "Date",
      flex: 1,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: "status",
      headerName: "Approve/Reject",
      flex: 1,
      renderCell: (params) => {
        const clr =
          params.value === "Approved"
            ? "success"
            : params.value === "Rejected"
              ? "error"
              : params.value === "Withdraw"
                ? "warning"
                : "default";

        return (
          <Badge color={clr} variant="outlined">
            {params.value === "Pending" ? "-" : params.value}
          </Badge>
        );
      },
    },
    {
      field: "approverRemarks",
      headerName: "Approver Remarks",
      flex: 1,
      renderCell: (params) => (params.value ? params.value : "-"),
    },
    {
      field: "shift",
      headerName: "Shift",
      flex: 1,
    },
    {
      field: "startTime",
      headerName: "In Time",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Field
            name={`rows.${params.row.id}.startTime`}
            sx={{ marginTop: "20px", marginBottom: "10px" }}
          >
            {({ field, form }) => (
              <TextField
                {...field}
                size="small"
                type="time"
                fullWidth
                value={field.value || ""}
                onChange={(e) => form.setFieldValue(field.name, e.target.value)}
                aria-readonly={params.row.status === "Pending"} // Enable/disable based on status
                disabled={params.row.status !== "Pending"} // Enable/disable based on status
              />
            )}
          </Field>
        </Box>
      ),
    },
    {
      field: "endTime",
      headerName: "Out Time",
      flex: 1,
      renderCell: (params) => (
        <Field name={`rows.${params.row.id}.endTime`}>
          {({ field, form }) => (
            <TextField
              {...field}
              size="small"
              type="time"
              fullWidth
              value={field.value || ""}
              onChange={(e) => form.setFieldValue(field.name, e.target.value)}
              aria-readonly={params.row.status === "Pending"} // Enable/disable based on status
              disabled={params.row.status !== "Pending"} // Enable/disable based on status
            />
          )}
        </Field>
      ),
    },
    {
      field: "session",
      headerName: "Session",
      flex: 1,
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 1,
    },
  ];

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const totalRows = applyedlist?.length;
  const displayedRows = applyedlist?.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // const buttons = [
  //   { type: "reset", label: "Cancel", size: "medium", variant: "outlined" },
  //   { type: "submit", label: "Withdraw", size: "medium", variant: "contained" },
  // ];

  const handleWithdrawClick = (item) => {
    // setSelectedItem({
    //   employeeId: item.empId,
    //   regularizationCode: item.regularizationCode,
    //   yearMonth: item.regularizationDates?.[0]?.slice(0, 7),
    // });
    setOpenWithdrawPopUp(true);
  };

  const handleWithdrawPopUpSubmit = async () => {
    if (!selectedRecord) return;
    try {
      const rawDate = selectedRecord?.regularizationDates?.[0];
      const formattedYearMonth = rawDate ? format(new Date(rawDate), "MM-yyyy") : "";
      await triggerGetData({
        empId: selectedRecord?.empId,
        regCode: selectedRecord?.regularizationCode,
        yearMonth: formattedYearMonth,
      }).unwrap();
      toast("success", "Regularization Withdraw");
      await refetch();
      await refetchHistory();
      setOpenWithdrawPopUp(false);
      setSelectedRecord(null);
      setShowViewList(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setOpenWithdrawPopUp(false);
    setSelectedRecord(null);
    setShowViewList(false);

  };

  const buttons = [
    { type: "reset", label: "Cancel", size: "medium", variant: "outlined" },
  ];

  // Show the "Withdraw" button only if tabName is "Pending"
  if (tabName === "Pending") {
    buttons.push({
      type: "submit",
      label: "Withdraw",
      size: "medium",
      variant: "contained",
    });
  }

  const handleButtonClick = (type) => {
    if (type === "reset") {
      setShowViewList(false); // Call reset function
    } else if (type === "submit") {
      handleWithdrawClick(); // Call submit function
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(date).toLocaleDateString(undefined, options);
  };
  const validationSchema = Yup.object().shape({
    rows: Yup.array().of(
      Yup.object().shape({
        startTime: Yup.string().required("Start Time is required"),
        endTime: Yup.string().required("End Time is required"),
      })
    ),
  });
  return (
    <Fragment>
      <style>
        {`
          .css-6l1m71-MuiTimeline-root {
            margin-bottom: -25px;
          }
        `}
      </style>
      <Formik
        initialValues={{ rows: applyedlist }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          withdraw();
        }}
      >
        {({ handleSubmit, resetForm }) => (
          <Fragment>
            <ToastContainer />
            <ApplicationTimeline
              approvelist={approvelist}
              formatDate={formatDate}
            />
            <Grid container spacing={2} marginTop={2}>
              <Grid item xs={9}>
                <Button variant="contained" onClick={() => setIsOpen(!isOpen)}>
                  Info
                </Button>
              </Grid>
              <Grid
                item
                xs={3}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <StyledBadge
                  name={"Regularization Applied Date"}
                  status={"Approved"}
                  chipBgColor={"#ECFDF3"}
                  labelColor={"#067647"}
                />
              </Grid>
            </Grid>{" "}
            <InfoCard
              isOpen={isOpen}
              toggle={() => setIsOpen(!isOpen)}
              approvelist={approvelist}
            />
            <Typography variant="h6" gutterBottom marginTop={2}>
              Regularization Applied Date
            </Typography>
            <Typography variant="body1">
              <b>SELF REMARKS:</b> {approvelist.employeeRemark}
            </Typography>
            <Form onSubmit={handleSubmit} marginTop={2}>
              <DataTableComponent
                displayedRows={applyedlist.map((row, index) => ({
                  ...row,
                  serialNo: index + 1,
                }))}
                // displayedRows={applyedlist}
                columns={columns}
                page={0}
                rowsPerPage={10}
                totalRows={applyedlist?.length}
                handlePageChange={handlePageChange}
                handleRowsPerPageChange={handleRowsPerPageChange}
              />
              <br />
              <br />
              <br />
              <Box sx={{ p: 0, display: "flex", justifyContent: "flex-end" }}>
                {buttons.map(({ type, label, size, variant }, index) => (
                  <Button
                    key={index}
                    type={type}
                    size={size}
                    variant={variant}
                    onClick={() => handleButtonClick(type)}
                    // onClick={
                    //   type === "reset" ? () => resetForm() : handleSubmit
                    // }
                    sx={{ ml: 1 }}
                  >
                    {label}
                  </Button>
                ))}
              </Box>
            </Form>
          </Fragment>
        )}
      </Formik>

      <CustomAlert
        open={openWithdrawPopUp}
        title="Withdraw Request"
        message="Are you sure you want to withdraw this application?"
        onCancel={withdrawClose}
        onSubmit={handleWithdrawPopUpSubmit}
        cancelText="Cancel"
        submitText="Submit"
      />

    </Fragment>
  );
};

export default ViewRegularizationList;
